// PaisContext.js — País del visitante
//
// Fuente de verdad para "en qué país está el visitante", que es lo que segmenta
// la landing (banner, textos, cursos…) además del subrol del usuario.
//
// Catálogo: `config_json.paises_config` del proyecto:
//   {
//     activo: true,                 // mostrar el selector en el header
//     por_defecto: 'CO',            // código del país inicial
//     recordar: true,               // guardar la elección en localStorage
//     items: [
//       { codigo:'CO', nombre:'Colombia', sigla:'COL', bandera:'', subrol:'', idioma:'' },
//       ...
//     ]
//   }
//
// Prioridad del país activo:
//   1. Elección manual del visitante (localStorage, solo sin sesión)
//   2. `users.pais` del usuario logueado (match por código o por nombre)
//   3. `por_defecto` del proyecto
//   4. Primer país del catálogo
//
// Un proyecto sin `paises_config` funciona exactamente como antes: el contexto
// entrega catálogo vacío y `pais = null`, y nadie pinta el selector.
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useProject } from './ProjectContext';
import { useAuth } from './AuthContext';
import { profileService } from '../services/api';
import { useIdioma } from './IdiomaContext';
import { normalizarPais } from '../utils/pais';

export { normalizarPais };

const PaisContext = createContext(null);

const LS_KEY = 'pais_visitante';


// ¿Este item del catálogo corresponde al valor dado (código, sigla o nombre)?
const coincide = (item, valor) => {
  const v = normalizarPais(valor);
  if (!v) return false;
  return [item.codigo, item.sigla, item.nombre].some(x => normalizarPais(x) === v);
};

export const PaisProvider = ({ children }) => {
  const { proyecto } = useProject();
  const { user, updateUser } = useAuth();
  const { idioma, setIdioma } = useIdioma();

  const cfg = proyecto?.config_json?.paises_config || null;
  const items = useMemo(
    () => (Array.isArray(cfg?.items) ? cfg.items.filter(p => p && (p.codigo || p.nombre)) : []),
    [cfg]
  );
  const recordar = cfg?.recordar !== false;

  // Elección manual del visitante (persistida). Solo aplica sin sesión: quien
  // inicia sesión ve el país de su cuenta.
  const [eleccion, setEleccion] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ''; } catch (_) { return ''; }
  });

  const paisUsuario = user?.pais || '';

  // Las sesiones abiertas antes de que el login empezara a devolver `subrol`
  // guardan un blob de usuario sin ese campo. Se rellena una vez desde /profile
  // para que la segmentación por perfil funcione sin obligar a volver a entrar.
  useEffect(() => {
    if (!user || user.subrol !== undefined) return;
    let vivo = true;
    profileService.getProfile()
      .then(res => {
        const u = res.data?.user || res.data;
        if (!vivo || !u) return;
        updateUser({ subrol: u.subrol ?? null, pais: u.pais ?? user.pais ?? null });
      })
      .catch(() => { if (vivo) updateUser({ subrol: null }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.subrol]);

  // Resolución del país activo según la prioridad de arriba.
  const paisActual = useMemo(() => {
    if (items.length === 0) return null;
    const candidatos = user
      ? [paisUsuario, cfg?.por_defecto]
      : [eleccion, cfg?.por_defecto];
    for (const c of candidatos) {
      if (!c) continue;
      const hit = items.find(p => coincide(p, c));
      if (hit) return hit;
    }
    return items[0];
  }, [items, user, paisUsuario, eleccion, cfg?.por_defecto]);

  // Si el usuario logueado tiene un país que sí está en el catálogo, se limpia
  // la elección de invitado para que no reaparezca al cerrar sesión con un país
  // distinto al de su cuenta.
  useEffect(() => {
    if (user && paisUsuario) {
      try { localStorage.removeItem(LS_KEY); } catch (_) { /* noop */ }
    }
  }, [user, paisUsuario]);

  // Idioma atado al país: si el item del catálogo define `idioma`, elegir ese
  // país cambia el idioma de la landing (Brasil → portugués). Se puede apagar
  // con `paises_config.sincronizar_idioma: false`; un catálogo sin `idioma` en
  // sus items no toca nada, que es como se comportan los proyectos actuales.
  const idiomaPais = paisActual?.idioma || null;
  useEffect(() => {
    if (cfg?.sincronizar_idioma === false) return;
    if (!idiomaPais || idiomaPais === idioma) return;
    setIdioma(idiomaPais);
  }, [idiomaPais, idioma, setIdioma, cfg?.sincronizar_idioma]);

  const setPais = useCallback((codigo) => {
    setEleccion(codigo || '');
    if (!recordar) return;
    try {
      if (codigo) localStorage.setItem(LS_KEY, codigo);
      else localStorage.removeItem(LS_KEY);
    } catch (_) { /* noop */ }
  }, [recordar]);

  const resetPais = useCallback(() => setPais(''), [setPais]);

  // Lo que consume la resolución de variantes de sección.
  // `paisIds` lleva TODAS las formas en que el admin pudo guardar este país en
  // una variante (código, sigla o nombre), para que la variante calce sin
  // importar cuál eligió.
  const audiencia = useMemo(() => ({
    pais: paisActual?.codigo || paisActual?.nombre || null,
    paisNombre: paisActual?.nombre || null,
    paisIds: paisActual
      ? [paisActual.codigo, paisActual.sigla, paisActual.nombre].filter(Boolean)
      : [],
    subrol: user?.subrol || paisActual?.subrol || null,
  }), [paisActual, user]);

  const value = useMemo(() => ({
    paises: items,
    paisActual,
    pais: paisActual?.codigo || null,
    setPais,
    resetPais,
    audiencia,
    // El selector solo tiene sentido con 2+ países y el catálogo activo.
    selectorDisponible: cfg?.activo !== false && items.length >= 2,
    eleccionManual: !!eleccion && !user,
  }), [items, paisActual, setPais, resetPais, audiencia, cfg, eleccion, user]);

  return <PaisContext.Provider value={value}>{children}</PaisContext.Provider>;
};

// Seguro fuera del provider: devuelve un estado vacío en lugar de romper, para
// que un componente reutilizado en el admin (previews) no falle.
const VACIO = {
  paises: [], paisActual: null, pais: null,
  setPais: () => {}, resetPais: () => {},
  audiencia: { pais: null, paisNombre: null, subrol: null },
  selectorDisponible: false, eleccionManual: false,
};

export const usePais = () => useContext(PaisContext) || VACIO;

export default PaisContext;
